import React, { memo } from "react";
import { Typography, Card, Input } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./GeneralInfoCard.css";
import useBasicData from "@/Profile/hooks/useBasicData";

const { Title } = Typography;

/* ==================== HELPERS ==================== */

const firstDefined = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== "");

const toArray = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === "string")
    {
      const trimmed = v.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((entry) => {
              if (typeof entry === "string") return entry.trim();
              return String(entry?.condition || entry?.label || entry?.name || "").trim();
            })
            .filter(Boolean);
        }
      } catch (_) {
        // Fall back to plain-text parsing below.
      }

      return trimmed
        .split(/[,;|]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  return [];
};

const formatNumberUnit = (val, unit) => {
  if (val === 0) return `0 ${unit}`;
  if (val === undefined || val === null || val === "") return "N/A";
  const s = String(val).trim();
  if (s === "") return "N/A";
  if (/\b(cm|cm\.|kg|kg\.)$/i.test(s)) return s;
  return `${s} ${unit}`;
};

const calcAge = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

/* ==================== COMPONENT ==================== */

function GeneralInfoCard({ user, medical, formatDate }) {
  const { t, i18n } = useTranslation();

  // Direction du texte selon la langue active (ar => rtl),
  // même logique que dans les autres composants.
  const isRTL = i18n.dir(i18n.language) === "rtl";

  /* ===== Basic user / snapshot data ===== */
  const normalizedUser = Array.isArray(user) ? user[0] : user;

  const gender = normalizedUser?.gender || t("generalInfo.na");
  const blood = firstDefined(medical?.groupeSanguin, t("generalInfo.na"));
  const age = calcAge(
    firstDefined(
      normalizedUser?.birthdate,
      normalizedUser?.birthDate,
      normalizedUser?.dateOfBirth
    )
  );
  const ageText =
    age !== null
      ? t("generalInfo.age.unit", { count: age, defaultValue: `${age} yrs` })
      : t("generalInfo.na");

  /* ===== Diseases (normalized) ===== */
  const diseasesArr = toArray(
    firstDefined(
      medical?.maladiesChroniques,
      medical?.diseases,
      medical?.maladies,
      medical?.pathologies
    )
  );
  const diseasesText =
    diseasesArr.length > 0 ? diseasesArr.join(", ") : t("generalInfo.none");

  /* ===== Latest visit ===== */
  const latestVisitRaw = firstDefined(
    medical?.latestVisit,
    medical?.lastVisitDate,
    medical?.dernierPassage,
    medical?.derniereVisite
  );

  const latestVisit = latestVisitRaw
    ? formatDate
      ? formatDate(latestVisitRaw)
      : String(latestVisitRaw)
    : t("generalInfo.na");

  /* ===== BMI data (SOURCE DE VÉRITÉ) ===== */
  const {
    height,
    weight,
    tempHeight,
    tempWeight,
    setTempHeight,
    setTempWeight,
    isEditing,
    saving,
  } = useBasicData();

  const heightText =
    height === undefined || height === null || height === ""
      ? t("generalInfo.na")
      : formatNumberUnit(height, "cm");

  const weightText =
    weight === undefined || weight === null || weight === ""
      ? t("generalInfo.na")
      : formatNumberUnit(weight, "kg");

  /* ==================== RENDER ==================== */

  return (
    <Card
      className="info-card"
      bordered={false}
      bodyStyle={{ padding: 0 }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="card-header">
        <Title level={5} className="card-title">
          {t("generalInfo.title")}
        </Title>
      </div>

      <div className="info-table">
        <div className="info-row">
          <div className="info-cell">
            <span className="info-label">{t("generalInfo.gender.label")}</span>
            <span className="info-value">{gender}</span>
          </div>
          <div className="info-cell">
            <span className="info-label">{t("generalInfo.age.label")}</span>
            <span className="info-value-center">{ageText}</span>
          </div>
        </div>

        <div className="info-row">
          <div className="info-cell">
            <span className="info-label">{t("generalInfo.bloodType.label")}</span>
            <span className="info-value">{blood}</span>
          </div>

          <div className="info-cell">
            <span className="info-label">{t("generalInfo.height.label")}</span>
            {!isEditing ? (
              <span className="info-value">{heightText}</span>
            ) : (
              <Input
                size="small"
                value={tempHeight}
                onChange={(e) => setTempHeight(e.target.value)}
                style={{ width: 120 }}
                placeholder={t("generalInfo.height.placeholder")}
                inputMode="decimal"
                disabled={saving}
              />
            )}
          </div>
        </div>

        <div className="info-row">
          <div className="info-cell">
            <span className="info-label">{t("generalInfo.weight.label")}</span>
            {!isEditing ? (
              <span className="info-value">{weightText}</span>
            ) : (
              <Input
                size="small"
                value={tempWeight}
                onChange={(e) => setTempWeight(e.target.value)}
                style={{ width: 120 }}
                placeholder={t("generalInfo.weight.placeholder")}
                inputMode="decimal"
                disabled={saving}
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}




GeneralInfoCard.propTypes = {
  user: PropTypes.object,
  medical: PropTypes.object,
  formatDate: PropTypes.func,
};

export default memo(GeneralInfoCard);